import React, { useState } from 'react';

const PricingComparison = () => {
  // Track activated column for an interactive mobile/desktop highlighting experience
  const [hoveredPlan, setHoveredPlan] = useState('professional');

  const features = [
    // Core & Package Features
    { name: 'WhatsApp Business Numbers', starter: '1 Number', professional: 'Unlimited', enterprise: 'Multi-Branch Automation', type: 'text' },
    { name: 'AI Message Credits', starter: '1,000 /mo', professional: '10,000 /mo', enterprise: 'Custom / Tailored', type: 'text' },
    { name: 'CRM User Access', starter: 'Unlimited Users', professional: 'Unlimited Users', enterprise: 'Unlimited + Branch Routing', type: 'text' },
    { name: 'Guided Setup Support', starter: true, professional: true, enterprise: 'White-Glove Onboarding', type: 'mixed' },
    
    // Automation & Intelligence
    { name: 'Local Language AI Training', starter: true, professional: 'Personalized Custom Training', enterprise: 'Fully Tailored AI Model', type: 'mixed' },
    { name: 'Natural Human-like AI Responses', starter: true, professional: true, enterprise: true, type: 'check' },
    { name: 'Custom AI Workflow Development', starter: false, professional: false, enterprise: true, type: 'check' },
    
    // Storefront & Sales Tools
    { name: 'Chat-based Checkout Flow', starter: true, professional: true, enterprise: true, type: 'check' },
    { name: 'Smart Delivery & Shipping Support', starter: true, professional: true, enterprise: true, type: 'check' },
    { name: 'Custom Web Storefront', starter: false, professional: 'Yes (with Product Listing)', enterprise: 'Fully Integrated Workflows', type: 'mixed' },
    
    // Integrations & Data
    { name: 'Centralized Order Hub', starter: true, professional: true, enterprise: true, type: 'check' },
    { name: 'System Sync (ERP, POS, Internal)', starter: false, professional: false, enterprise: 'Full Integration Sync', type: 'mixed' },
    { name: 'Data Export Options', starter: 'CSV Download', professional: 'Advanced Export & Analytics', enterprise: 'Custom Analytics Dashboard', type: 'text' },
    
    // Analytics & Business Support
    { name: 'Performance Reports', starter: 'Basic Summary', professional: 'Sales & Inquiry Insights', enterprise: 'Custom Branch Analytics', type: 'text' },
    { name: 'Dedicated Business Support', starter: false, professional: 'Dedicated Success Manager', enterprise: 'Priority Support + Strategy Meetings', type: 'mixed' },
  ];

  // Premium Custom Micro-Icons
  const CheckIcon = () => (
    <div className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500/10 border border-emerald-500/30 shadow-[0_0_12px_rgba(16,185,129,0.2)]">
      <svg className="w-2.5 h-2.5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      </svg>
    </div>
  );

  const CrossIcon = () => (
    <div className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/[0.02] border border-white/5 text-neutral-700">
      <svg className="w-2 h-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </div>
  );

  return (
    <section className="bg-[#030705] text-white py-24 px-4 border-t border-neutral-900 relative overflow-hidden">
      
      {/* Dynamic Background Studio Glows */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[70vw] h-[35vh] bg-[radial-gradient(circle,rgba(16,185,129,0.04)_0%,transparent_70%)] blur-[80px] pointer-events-none" />
      <div className="absolute -bottom-10 right-10 w-[300px] h-[300px] bg-emerald-500/[0.02] blur-[120px] pointer-events-none" />

      <div className="max-w-6xl mx-auto relative z-10">
        
        {/* Header */}
        <div className="max-w-[700px] lg:max-w-[600px] mx-auto text-center mb-12 sm:mb-16 flex flex-col items-center">
          <span className="inline-flex items-center gap-[8px] px-3.5 py-1 rounded-full bg-white/[0.03] backdrop-blur-md border border-[var(--main-green-color)] text-xs font-medium text-[var(--main-green-color)] tracking-tight mb-5 shadow-sm select-none">
            Capabilities Dashboard
          </span>
          <h2 className="text-2xl sm:text-4xl lg:text-5xl tracking-[-0.03em] leading-[1.2] font-semibold text-emerald-100">
            Compare Full <span className="text-[var(--main-green-color)]">Features</span>
          </h2>
          <p className="text-neutral-400 mt-4 text-sm sm:text-[.9rem] leading-relaxed">
            Choose the ultimate automated selling powerhouse built specifically for modern social commerce.
          </p>
        </div>

        {/* Modern Bento Comparison Grid Table Container */}
        <div className="overflow-x-auto rounded-2xl border border-white/5 bg-[#070908]/40 backdrop-blur-xl shadow-[0_24px_60px_-15px_rgba(0,0,0,0.8)]">
          <table className="w-full text-left border-collapse min-w-[850px] table-fixed">
            <thead>
              <tr className="border-b border-white/5 bg-white/[0.01]">
                {/* Metric Info Column */}
                <th className="p-6 text-xs text-neutral-400 font-medium w-[28%] align-middle">
                  <span className="text-[10px] font-mono tracking-wider uppercase opacity-50 block mb-1">Matrix Specs</span>
                  <span className="text-white text-base font-semibold tracking-tight">Feature Engine</span>
                </th>
                
                {/* Starter Plan Column */}
                <th 
                  className={`p-6 text-center w-[24%] transition-all duration-300 relative cursor-pointer ${hoveredPlan === 'starter' ? 'bg-white/[0.02]' : ''}`}
                  onMouseEnter={() => setHoveredPlan('starter')}
                >
                  <span className="block text-[11px] text-neutral-500 font-mono tracking-widest uppercase mb-1">01 // Entry</span>
                  <span className="text-xl font-bold text-white tracking-tight">Starter</span>
                  <div className="mt-1 flex items-baseline justify-center gap-0.5">
                    <span className="text-base sm:text-lg font-extrabold text-white tracking-tight">LKR 15,000</span>
                    <span className="text-neutral-500 text-xs font-normal">/mo</span>
                  </div>
                </th>
                
                {/* Professional Plan Column (UX Highlighted) */}
                <th 
                  className={`p-6 text-center w-[24%] transition-all duration-300 relative cursor-pointer ${hoveredPlan === 'professional' ? 'bg-emerald-500/[0.03]' : ''}`}
                  onMouseEnter={() => setHoveredPlan('professional')}
                >
                  <div className={`absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-emerald-500 to-teal-400 transition-opacity duration-300 ${hoveredPlan === 'professional' ? 'opacity-100' : 'opacity-40'}`} />
                  
                  <span className="inline-flex items-center bg-emerald-500/10 text-emerald-400 text-[9px] font-mono font-bold px-2 py-0.5 rounded-full uppercase tracking-wider mb-2">
                    Most Popular
                  </span>
                  <span className="block text-xl font-bold text-white tracking-tight">Professional</span>
                  <div className="mt-1 flex items-baseline justify-center gap-0.5">
                    <span className="text-base sm:text-lg font-extrabold text-emerald-400 tracking-tight">LKR 35,000</span>
                    <span className="text-neutral-500 text-xs font-normal">/mo</span>
                  </div>
                </th>
                
                {/* Enterprise Plan Column */}
                <th 
                  className={`p-6 text-center w-[24%] transition-all duration-300 relative cursor-pointer ${hoveredPlan === 'enterprise' ? 'bg-white/[0.02]' : ''}`}
                  onMouseEnter={() => setHoveredPlan('enterprise')}
                >
                  <span className="block text-[11px] text-neutral-500 font-mono tracking-widest uppercase mb-1">03 // Scale</span>
                  <span className="text-xl font-bold text-white tracking-tight">Enterprise</span>
                  <div className="mt-1 flex items-baseline justify-center gap-0.5">
                    <span className="text-base sm:text-lg font-extrabold text-white tracking-tight">LKR 99,000+</span>
                    <span className="text-neutral-500 text-xs font-normal">/mo</span>
                  </div>
                </th>
              </tr>
            </thead>
            
            <tbody>
              {features.map((row, index) => (
                <tr 
                  key={index} 
                  className="border-b border-white/[0.03] hover:bg-white/[0.01] transition-colors duration-200 group"
                >
                  {/* Feature Label Description */}
                  <td className="p-5 text-sm text-neutral-300 font-normal group-hover:text-white transition-colors duration-200">
                    {row.name}
                  </td>
                  
                  {/* Starter Column Data */}
                  <td className={`p-5 text-center text-xs transition-all duration-300 ${hoveredPlan === 'starter' ? 'bg-white/[0.015]' : ''}`}>
                    {row.type === 'check' && (row.starter ? <CheckIcon /> : <CrossIcon />)}
                    {row.type === 'text' && <span className="font-medium text-neutral-400 group-hover:text-neutral-200">{row.starter}</span>}
                    {row.type === 'mixed' && (
                      typeof row.starter === 'boolean' 
                        ? (row.starter ? <CheckIcon /> : <CrossIcon />) 
                        : <span className="font-medium text-neutral-400 group-hover:text-neutral-200">{row.starter}</span>
                    )}
                  </td>
                  
                  {/* Professional Column Data */}
                  <td className={`p-5 text-center text-xs transition-all duration-300 ${hoveredPlan === 'professional' ? 'bg-emerald-500/[0.02]' : ''}`}>
                    {row.type === 'check' && (row.professional ? <CheckIcon /> : <CrossIcon />)}
                    {row.type === 'text' && <span className="font-semibold text-emerald-400">{row.professional}</span>}
                    {row.type === 'mixed' && (
                      typeof row.professional === 'boolean' 
                        ? (row.professional ? <CheckIcon /> : <CrossIcon />) 
                        : <span className="font-semibold text-emerald-400">{row.professional}</span>
                    )}
                  </td>
                  
                  {/* Enterprise Column Data */}
                  <td className={`p-5 text-center text-xs transition-all duration-300 ${hoveredPlan === 'enterprise' ? 'bg-white/[0.015]' : ''}`}>
                    {row.type === 'check' && (row.enterprise ? <CheckIcon /> : <CrossIcon />)}
                    {row.type === 'text' && <span className="font-medium text-neutral-300 group-hover:text-white">{row.enterprise}</span>}
                    {row.type === 'mixed' && (
                      typeof row.enterprise === 'boolean' 
                        ? (row.enterprise ? <CheckIcon /> : <CrossIcon />) 
                        : <span className="font-medium text-neutral-300 group-hover:text-white">{row.enterprise}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
      </div>
    </section>
  );
};

export default PricingComparison;